import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { IHistoryMailService } from '@commons/history/history-mail/hitsory-mail.service';
import { MailComment } from '@commons/history/history-mail/mail-comment/models/mail-comment';
import { IEpticaExctractAttachment, IEpticaExctractEvent } from '@commons/history/history-mail/models/eptica-extract';
import { EpticaSelector } from '@commons/history/history-mail/models/eptica-selector';
import { destroy, RxUtil } from '@commons/util/utilities/rx';
import { ILogService, IPromise, IQService, ITimeoutService } from 'angular';
import { i18n } from 'i18next';
import { BehaviorSubject, combineLatest, map, Observable, of, Subscription, switchMap } from 'rxjs';

import {
  BookingFacade,
  ChannelDirection,
  EpticaEnterpriseAgentExposed,
  EpticaExposedCustomer,
  EpticaExposedRequest,
  EpticaService,
  isNotNullOrUndefined,
  MailHistoryMode,
  MomentService,
  None,
  Option,
  ProfileFacade,
  ProfileService,
} from '@contact/upgrade';

import { IEpticaDeprecatedService } from '../eptica-deprecated.service';
import { EpticaHelper } from '../eptica.helper';
import { EpticaHelperJson } from '../eptica.helper.json';
import { EpticaButtonActionTypes, EpticaData, EpticaDropZoneElements } from './eptica-data';
import {
  ACCOUNT_GROUP_POPUP_LIST_PAGE_NAME,
  ACCOUNT_GROUP_POPUP_PAGE_NAME,
  ACCOUNT_LIST_POPUP_PAGE_NAME,
  EpticaPage,
  IEpticaJsonResponse,
  IEpticaRequestFrame,
  UpdateRecipientFields,
} from './eptica.request.interfaces';
import { ServiceWrapper } from './service-wrapper';

export class EpticaRequest extends EpticaPage implements IEpticaRequestFrame {
  private sw: ServiceWrapper;
  get events$() {
    return this._events$;
  }
  private _events$ = new BehaviorSubject<IEpticaExctractEvent[]>([]);
  get requestDto$() {
    return this._requestDto$.pipe(isNotNullOrUndefined());
  }
  readonly _requestDto$ = new BehaviorSubject<EpticaExposedRequest | undefined>(undefined);
  get customerDto$() {
    return this._customerDto$.pipe(isNotNullOrUndefined());
  }
  private _customerDto$ = new BehaviorSubject<EpticaExposedCustomer | undefined>(undefined);
  private csrLastName: string;
  private csrFirstName: string;
  private lastnameSub: Subscription;
  private firstnameSub: Subscription;
  closeComment: string | undefined;
  private readonly _updateRecipients$ = new BehaviorSubject<UpdateRecipientFields | undefined>(undefined);
  readonly updateRecipients$ = this._updateRecipients$.pipe(isNotNullOrUndefined());

  constructor(
    protected override readonly frameId: string,
    private readonly requestId: number,
    protected override readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly $i18next: i18n,
    protected override readonly extApp: AppExternalService,
    protected override readonly toastDeprecatedService: ToastDeprecatedService,
    protected override readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly epticaService: EpticaService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    protected readonly historyMailService: IHistoryMailService,
    protected readonly contactFacade: BookingFacade,
    private readonly profileService: ProfileService,
    private timeUtil: MomentService,
    private profileFacade: ProfileFacade,
    private contactDeprecatedService: IContactDeprecatedService,
    private readonly $timeout: ITimeoutService
  ) {
    super(frameId, extApp, toastDeprecatedService, epticaDeprecatedService, $log);
    this.sw = {
      $log: this.$log,
      $q: this.$q,
      extApp: this.extApp,
      toastDeprecatedService: this.toastDeprecatedService,
      $i18next: this.$i18next,
      sessionDeprecatedService: this.sessionDeprecatedService,
    };
    // extract events
    this.historyMailService
      .extractEvents(requestId, this.epticaDeprecatedService.getBaseUrlStr(), this.epticaDeprecatedService.getEpticaLocal(), frameId)
      .subscribe(events => this._events$.next(events));
    // cached request
    this.epticaService
      .getRequest(this.requestId)
      .pipe(
        isNotNullOrUndefined(),
        switchMap(request =>
          request.customer
            ? this.epticaService.getCustomer(request.customer.id).pipe(map(customer => ({ request, customer })))
            : of({ request, customer: undefined })
        )
      )
      .subscribe(({ request, customer }) => {
        this._requestDto$.next(request);
        this._customerDto$.next(customer);
      });

    this.lastnameSub = this.profileFacade.lastname$.subscribe(lastname => (this.csrLastName = lastname));
    this.firstnameSub = this.profileFacade.firstname$.subscribe(firstname => (this.csrFirstName = firstname));

    this.contactDeprecatedService.getSelectedContact().then(contact => {
      if (
        this.isMessageTextEmpty() &&
        !!contact &&
        !!contact.entryPoint &&
        contact.entryPoint.mailHistoryMode === MailHistoryMode.ENABLED
      ) {
        this.copyLastMailHistory();
      }
    });
  }

  updateRecipients(status: UpdateRecipientFields) {
    this._updateRecipients$.next(status);
  }

  // copy last mail historic to editor
  copyLastHistoryToEditor(
    historicFrom: string,
    historicTo: string,
    historicCc: string,
    historicSubject: string,
    historicDateTime: string,
    historicContentHtml: string,
    $i18next: i18n
  ): void {
    this.doInEpticaFrame(frame =>
      EpticaHelper.copyLastHistoryToEditor(
        frame,
        historicFrom,
        historicTo,
        historicCc,
        historicSubject,
        historicDateTime,
        historicContentHtml,
        $i18next
      )
    );
  }

  private copyLastMailHistory() {
    // Get eptica conversational events
    this.doInEpticaFrame(() => {
      // Retrieve conversational events for this request
      this.retrieveConversationalEvents()
        .pipe(destroy(this))
        .subscribe(epticaEvents => {
          if (!!epticaEvents && epticaEvents.length > 0) {
            const [event] = epticaEvents.slice().sort((a, b) => b.date.getTime() - a.date.getTime());
            const form = (this.isIncomingMessage(event) ? event.sender : `${this.csrFirstName} ${this.csrLastName}`) || '';
            const recipient = event.recipient || '';
            const cc = event.ccRecipient || '';
            const subject = event.subject || '';
            const dateTime = this.timeUtil.readableDateTime(event.date);
            const contentHtml = event.contentHtml || '';
            // insert the last client response and information
            this.copyLastHistoryToEditor(form, recipient, cc, subject, dateTime, contentHtml, this.$i18next);
          }
        });
    });
  }

  private isIncomingMessage(event: IEpticaExctractEvent): boolean {
    return (
      !!event &&
      (event.selector === EpticaSelector.request_event_type_client_message ||
        event.selector === EpticaSelector.request_event_type_opening ||
        event.selector === EpticaSelector.request_event_type_monitoring)
    );
  }

  close(comment?: string): IPromise<any> {
    return RxUtil.toPromise(this.epticaDeprecatedService.closeRequest(this.requestId, comment), this.$q);
  }

  getId(): number {
    return this.requestId;
  }

  toggleDelegatePanel(show: boolean): boolean {
    try {
      this.doInEpticaFrame(frame => {
        if (show) {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'appear', 'request-delegate');
          this.toggleAnswerPanel(false);
          this.toggleClosePanel(false);
          this.toggleAssistPanel(false);
        } else {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'disappear', 'request-delegate', 'delegate');
        }
      });
    } catch (e) {
      this.$log.error(e);
    }
    return true;
  }

  toggleAnswerPanel(show: boolean): boolean {
    try {
      this.doInEpticaFrame(frame => {
        if (show) {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'appear', 'request-answer');
          this.toggleDelegatePanel(false);
          this.toggleAssistPanel(false);
        } else {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'disappear', 'request-answer', 'formAnswerRequest');
        }
      });
    } catch (e) {
      this.$log.error(e);
    }
    return true;
  }

  toggleClosePanel(show: boolean): boolean {
    try {
      if (show) {
        this.toggleDelegatePanel(false);
        this.toggleAnswerPanel(false);
        this.toggleAssistPanel(false);
        this.toggleRecipientAddress(false);
      } else {
        this.toggleAnswerPanel(true);
        this.toggleRecipientAddress(true);
      }
    } catch (e) {
      this.$log.error(e);
    }
    return true;
  }

  toggleAssistPanel(show: boolean): boolean {
    try {
      this.doInEpticaFrame(frame => {
        if (show) {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'appear', 'request-ask-for-assistance');
          this.toggleDelegatePanel(false);
          this.toggleAnswerPanel(false);
          this.toggleClosePanel(false);
        } else {
          EpticaHelper.callEpticaFn<void>(frame, this.$log, 'disappear', 'request-ask-for-assistance', 'askAssistance');
        }
      });
    } catch (e) {
      this.$log.error(e);
    }
    return true;
  }

  appearRequestAnswerAssistance(): boolean {
    try {
      this.doInEpticaFrame(frame => EpticaHelper.callEpticaFn<void>(frame, this.$log, 'appear', 'request-answer-assistance'));
    } catch (e) {
      this.$log.debug(e);
    }
    return true;
  }

  uniformUploadButtonForAskForHelp(): void {
    this.doInEpticaFrame(frame => EpticaHelper.uniformUploadButtonForAskForHelp(this.$i18next, frame));
  }

  hasOpenedPopup(): boolean {
    return this.getInEpticaFrame(frame => EpticaHelper.getEpticaData(frame), None)
      .map(
        epticaData =>
          epticaData.pageName === ACCOUNT_LIST_POPUP_PAGE_NAME ||
          epticaData.pageName === ACCOUNT_GROUP_POPUP_PAGE_NAME ||
          epticaData.pageName === ACCOUNT_GROUP_POPUP_LIST_PAGE_NAME
      )
      .getOrElse(false);
  }

  send(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)) {
        return EpticaHelperJson.sendAndCloseClick(this.sw, frame);
      }
      EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      return this.$q.reject();
    });
  }

  addCcAndCccContacts(): void {
    this.doInEpticaFrame(frame => this.createCcAndBccContacts(frame));
  }

  protected getDirection(): ChannelDirection {
    return ChannelDirection.INCOMING;
  }

  reOpen(): IPromise<EpticaData> {
    return this.doPromiseInEpticaFrame(frame =>
      EpticaHelper.submitFormAndWailtForLoad(
        this.sw.$q,
        this.sw.$i18next,
        this.sw.$log,
        this.sw.toastDeprecatedService,
        this.sw.sessionDeprecatedService,
        frame,
        'openRequestForm'
      )
    );
  }

  getEpticaCustomer(requestId: string): string {
    let ret = '';
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      let _customer = EpticaHelper.callEpticaFn(frame, undefined, 'getRequestCustomer', requestId);
      if (_customer.isDefined()) {
        // get the customer's email address
        return EpticaHelper.callEpticaFn<string>(frame, undefined, 'getCustomerFieldValue', _customer.get(), 'EMail').getOrElse('');
      }
      return None;
    });

    return ret;
  }

  getEpticaFieldValue(fieldName: string): string {
    let ret = '';
    this.doInEpticaFrame(frame => {
      let newEMailInput = EpticaHelper.getEpticaElementById(frame, fieldName);
      if (!!newEMailInput) {
        ret = newEMailInput.getAttribute('value') || '';
      }
    });
    return ret;
  }

  sendDefer(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)) {
        return EpticaHelperJson.sendAndDeferClick(this.sw, frame);
      }
      EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      return this.$q.reject();
    });
  }

  defer(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      if (
        !EpticaHelper.isMessageTextEmpty(frame) &&
        EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)
      ) {
        return EpticaHelperJson.saveDraftClick(this.sw, frame);
      }
      EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      return this.$q.reject();
    });
  }

  delegate(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      if (this.validateDelegate(frame)) {
        // check if there is a draft
        if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'requestHasDrafts').getOrElse(false)) {
          if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'forwardedToNonNetworkAgent').getOrElse(false)) {
            let deferred = this.sw.$q.defer<IEpticaJsonResponse>();
            let buttonContinue: HTMLElement = EpticaHelper.getEpticaElementBySelector(
              frame,
              'button[onclick^="draftLostContinueCallback"]'
            );
            if (buttonContinue) {
              EpticaHelper.simulateClick(frame, buttonContinue);
              deferred.resolve();
            } else {
              deferred.reject('cannot validate draft lost continue form');
            }
            return deferred.promise;
          }
        }
        return EpticaHelperJson.delegateClick(this.sw, frame);
      } else {
        EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-delegate');
      }
      return this.sw.$q.resolve({
        messageState: '',
        messageLabel: '',
      });
    });
  }

  sendDelegate(): IPromise<EpticaData> {
    return this.doPromiseInEpticaFrame(frame => {
      if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)) {
        return EpticaHelper.sendAndCloseClick(
          this.$q,
          this.$i18next,
          this.$log,
          this.toastDeprecatedService,
          this.sessionDeprecatedService,
          frame
        );
      } else {
        EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      }
      return this.$q.reject();
    });
  }

  validateDelegate(frame: HTMLIFrameElement): boolean {
    let isMessageSelected: boolean = EpticaHelper.callEpticaFn<boolean>(
      frame,
      this.$log,
      'isMessageSelected',
      '#delegateRequest',
      '#messageSelectionModalForward',
      false
    ).getOrElse(false);
    let hasTextMessage: boolean =
      (EpticaHelper.getEpticaElementById(frame, 'delegateRequest_delegateMessage_textMessage') as any).value !== '';
    return isMessageSelected && hasTextMessage;
  }

  cancelAssist(): boolean {
    this.toggleAnswerPanel(true);
    return this.getInEpticaFrame(frame => {
      EpticaHelper.cancelAssistClick(frame);
      return true;
    }, false);
  }

  assist(epticaRequestId: number): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => EpticaHelperJson.assistClick(this.sw, frame)).then(frame => {
      this.epticaDeprecatedService.assistRequest(epticaRequestId);
      return frame;
    });
  }

  acceptApproval(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => EpticaHelperJson.acceptApprovalClick(this.sw, frame)).then(frame => {
      this.$timeout(500).then(() => RxUtil.toPromise(this.epticaDeprecatedService.acceptApprovalRequest(this.requestId), this.$q));
      return frame;
    });
  }

  requestRejectApproval(cancelAction: () => void, validateAction: () => void): boolean {
    this.toggleDelegatePanel(false);
    this.toggleClosePanel(false);

    let success = false;
    this.doInEpticaFrame(frame => {
      if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)) {
        EpticaHelper.requestRejectApprovalClick(frame);
        success = true;

        // change cross onclick
        let button = frame.contentWindow!.document.querySelector('#rejectReview_promptPopup button.close') as HTMLButtonElement;
        if (button) {
          button.onclick = event => {
            event.preventDefault();
            cancelAction();
          };

          // change cancel button behaviour
          let container = button.ownerDocument!.getElementById('rejectReview_promptPopupValidateButton');
          if (container && container.parentNode) {
            EpticaHelper.createButton(container.parentNode as HTMLElement, '[Valider]', validateAction, true);
            EpticaHelper.createButton(container.parentNode as HTMLElement, '[Annuler]', cancelAction, false);
          }
        }
      } else {
        EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      }
    });
    return success;
  }

  rejectApproval(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      // Retrieve reject approval comment
      let commentElement: HTMLTextAreaElement = frame.contentWindow!.document.getElementById(
        'rejectReview_promptField'
      ) as HTMLTextAreaElement;
      if (!commentElement) {
        // eptica >= 9.6
        commentElement = frame.contentWindow!.document.getElementById('rejectReview_rejectReview_comment') as HTMLTextAreaElement;
      }
      const comment: string = commentElement ? commentElement.value : '';

      // reject approval Validation
      //
      // Eptica are created with JS and template so it's difficult to catch validation
      // method and validation callback (see /ias/resources/js/eptica/popup/promptPopup.js)
      // Here, the validation method just check that the input askForReviewComment is not empty
      // We consider that the validation fails if and only if this field is empty
      // If it fail, we retrieve the error message and create a toast with it

      if (comment !== '') {
        return EpticaHelperJson.rejectApprovalClick(this.sw, frame).then(response => {
          this.$timeout(500).then(() => RxUtil.toPromise(this.epticaDeprecatedService.rejectApprovalRequest(this.requestId), this.$q));
          return response;
        });
      }
      return this.$q.reject();
    });
  }

  cancelRejectApproval(): boolean {
    this.toggleAnswerPanel(true);
    return this.getInEpticaFrame(frame => {
      EpticaHelper.cancelRejectApprovalClick(frame);
      return true;
    }, false);
  }

  retrieveEvents(...selector: EpticaSelector[]): Observable<IEpticaExctractEvent[]> {
    if (selector && selector.length > 0) {
      return this._events$.pipe(map(events => events.filter(evt => selector.some(s => evt.selector === s))));
    } else {
      return this._events$;
    }
  }

  retrieveConversationalEvents(): Observable<IEpticaExctractEvent[]> {
    return this.retrieveEvents(
      EpticaSelector.request_event_type_client_message,
      EpticaSelector.request_event_type_response,
      EpticaSelector.request_event_type_opening,
      EpticaSelector.request_event_type_outgoing_mail,
      EpticaSelector.request_event_type_monitoring,
      EpticaSelector.request_event_type_followup
    ).pipe(
      map(events =>
        events.filter(event => {
          switch (event.selector) {
            // Eptica used the same 'request_event_type_opening' event for the reoping by agent or client
            // we only want to display the reoping by client
            // in the agent case there is no message
            // so we test the message content to determinate the isConversational
            case EpticaSelector.request_event_type_opening:
            case EpticaSelector.request_event_type_followup:
              return !!event.recipient || !!event.ccRecipient || !!event.subject || !!event.content;
            default:
              return true;
          }
        })
      )
    );
  }

  retrieveApprovalEvents(): Observable<IEpticaExctractEvent[]> {
    return this.retrieveEvents(
      EpticaSelector.request_event_type_review_requested,
      EpticaSelector.request_event_type_rejected_by_supervisor,
      EpticaSelector.request_event_type_approved_by_supervisor
    );
  }

  retrieveAskForHelpEvents(): Observable<IEpticaExctractEvent[]> {
    return this.retrieveEvents(EpticaSelector.request_event_ask_for_help);
  }

  retrieveLastTransferEvent(): Observable<IEpticaExctractEvent | undefined> {
    const forward$ = this.retrieveEvents(
      EpticaSelector.request_event_type_forwarded_with_attachments,
      EpticaSelector.request_event_type_forwarded_by_agent_to_group,
      EpticaSelector.request_event_type_forwarded
    );
    const bounce$ = this.retrieveEvents(EpticaSelector.request_event_type_bounce_by_agent).pipe(
      map(events => events.filter(evt => MailComment.isForwardType(evt.bounceType)))
    );
    return combineLatest(forward$, bounce$).pipe(
      map(([forward, bounce]) => [...forward, ...bounce]),
      map(events => [...events].sort((a, b) => b.date.getTime() - a.date.getTime())),
      map(([evt]) => evt)
    );
  }

  retrieveSubject(): Observable<string> {
    return this.retrieveEvents(EpticaSelector.request_event_type_client_message).pipe(
      map(([evt]) =>
        Option.of(evt)
          .map(e => e.subject)
          .getOrElse(' ')
      )
    );
  }

  override preRemoveRequest(): void {
    super.preRemoveRequest();
    this._events$.complete();
    this._events$.unsubscribe();
    this._requestDto$.complete();
    this._requestDto$.unsubscribe();
    this._customerDto$.complete();
    this._customerDto$.unsubscribe();
    this.epticaDeprecatedService.removePJEventsForLoader();
    this.lastnameSub.unsubscribe();
    this.firstnameSub.unsubscribe();
    this._updateRecipients$.complete();
  }

  askForReview(): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame => {
      // Retrieve ask for review review comment
      let commentElement: HTMLTextAreaElement = frame.contentWindow!.document.getElementById(
        'askForReview_promptField'
      ) as HTMLTextAreaElement;
      if (!commentElement) {
        // eptica >= 9.6
        commentElement = frame.contentWindow!.document.getElementById('askForReview_askForReview_comment') as HTMLTextAreaElement;
      }
      const comment: string = commentElement ? commentElement.value : '';

      // Ask For Review Validation
      //
      // Eptica are created with JS and template so it's difficult to catch validation
      // method and validation callback (see /ias/resources/js/eptica/popup/promptPopup.js)
      // Here, the validation method just check that the input askForReviewComment is not empty
      // We consider that the validation fails if and only if this field is empty
      // If it fail, we retrieve the error message and create a toast with it

      if (!!comment && comment !== '') {
        return EpticaHelperJson.askForReview(this.sw, frame, comment).then(response => {
          this.$timeout(500).then(() => RxUtil.toPromise(this.epticaDeprecatedService.askApprovalRequest(this.requestId), this.$q));
          return response;
        });
      }
      return this.$q.reject();
    });
  }

  bounce(responseId: string): IPromise<any> {
    return this.doPromiseInEpticaFrame(frame =>
      EpticaHelper.cancelPendingActions(this.sw, frame, () =>
        this.epticaDeprecatedService.bounceRequest(this.getId(), MailComment.toComment('BOUNCE', new Date()), responseId)
      )
    );
  }

  overrideClosePendingAssistJson(confirmAction: () => IPromise<IEpticaJsonResponse>): void {
    this.doInEpticaFrame(frame => EpticaHelper.overrideClosePendingAssist(frame, confirmAction));
  }

  overrideClosePendingAssist(confirmAction: () => IPromise<EpticaData>): void {
    this.doInEpticaFrame(frame => EpticaHelper.overrideClosePendingAssist(frame, confirmAction));
  }

  confirmClosePendingAssist(confirmButtonId: string): IPromise<EpticaData> {
    return this.doPromiseInEpticaFrame(frame =>
      EpticaHelper.confirmClosePendingAssist(
        this.$q,
        this.$i18next,
        this.$log,
        this.toastDeprecatedService,
        this.sessionDeprecatedService,
        frame,
        confirmButtonId
      )
    );
  }

  confirmClosePendingAssistJson(submitForm: () => IPromise<IEpticaJsonResponse>): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(() => EpticaHelperJson.confirmClosePendingAssist(submitForm));
  }

  addCustomClass(classStr: string) {
    this.doInEpticaFrame(frame => EpticaHelper.addCustomClass(frame, classStr));
  }

  addClassToEditorFormContainer() {
    this.doInEpticaFrame(frame => EpticaHelper.addClassToEditorFormContainer(frame));
  }

  validateRequestAskForReview(comment: string): IPromise<boolean> {
    const deferred = this.$q.defer<boolean>();
    let success = false;
    this.doInEpticaFrame(frame => {
      EpticaHelper.requestAskForReviewClick(this.$q, frame).then(() => {
        let commentArea = frame.contentWindow!.document.querySelector('#askForReview_promptField');
        if (commentArea) {
          (commentArea as HTMLTextAreaElement).value = comment;
          success = true;
        } else {
          // eptica >= 9.6
          commentArea = frame.contentWindow!.document.querySelector('#askForReview_askForReview_comment');
          if (commentArea) {
            (commentArea as HTMLTextAreaElement).value = comment;
            success = true;
          }
        }
        deferred.resolve(success);
      });
    });

    return deferred.promise;
  }

  requestAskForReview(): boolean {
    this.toggleDelegatePanel(false);
    this.toggleClosePanel(false);

    let success = false;
    this.doInEpticaFrame(frame => {
      if (EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false)) {
        success = true;
      } else {
        EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      }
    });
    return success;
  }

  cancelAskForReview(): boolean {
    this.toggleAnswerPanel(true);
    this.doInEpticaFrame(frame => EpticaHelper.cancelAskForReviewClick(frame));
    return true;
  }

  openKnowledgeDialog(cancelAction: () => void, validateAction: () => void): boolean {
    this.toggleDelegatePanel(false);
    this.toggleClosePanel(false);

    this.doInEpticaFrame(frame => {
      // Opens the dialog
      EpticaHelper.browseKonwledgeOutgoingClick(frame);

      // Deals with cancel events
      window.setTimeout(() => EpticaHelper.overrideKnowledgeBehaviour(frame), 300);

      // Replace cancel action
      let popupContainer = frame.contentWindow!.document.getElementById('browse-kb-popup');
      if (popupContainer) {
        EpticaHelper.removeAddedButtons(popupContainer);
        EpticaHelper.overrideKnowledgeDialogCancel(popupContainer, cancelAction);
      }

      // change cancel button behaviour
      let container = frame.contentWindow!.document.getElementById('insert-answer-button');
      if (container && container.parentNode) {
        EpticaHelper.createButton(
          container.parentNode as HTMLElement,
          this.$i18next.t('agent:contact.mailactions.buttonInsertKnowledge'),
          validateAction,
          true
        );
      }
    });
    return true;
  }

  validateKnowlegdeDialog(): boolean {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        let answerButton = frame.contentWindow!.document.getElementById('insert-answer-button');
        if (answerButton && answerButton.style.display !== 'none') {
          EpticaHelper.insertKnowlegdeClick(frame);
          EpticaHelper.hideAttachmentsWithZeroSize(frame);
        } else {
          throw null;
        }
        return true;
      })
      .getOrElse(false);
  }

  cancelKnowlegdeDialog(): boolean {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => {
        EpticaHelper.closeEpticaModal(frame, 'browse-kb-popup');
        return true;
      })
      .getOrElse(false);
  }

  cancelKnowlegdeDialogVanilla(): boolean {
    return this.getInEpticaFrame(frame => {
      EpticaHelper.simulateClickOnSelection(frame, '#browse-kb-popup button.close:not(.addedByContact)', true, false);
      EpticaHelper.simulateClick(frame, 'browse-kb-popup');
      return true;
    }, false);
  }

  getPageName(): string {
    return this.getInEpticaFrame(frame => EpticaHelper.getEpticaData(frame), None)
      .map(epticaData => epticaData.pageName)
      .getOrElse('');
  }

  // Add two icons for print and preview
  addPrintAndPreviewButtons(): void {
    this.doInEpticaFrame(frame => EpticaHelper.addPrintAndPreviewButtons(this.$i18next, this.sessionDeprecatedService, frame));
  }

  // Add two icons for print and preview (for assistance requests)
  addPrintButtonForAssists(): void {
    this.doInEpticaFrame(frame => EpticaHelper.addPrintButtonForAssists(this.$i18next, this.sessionDeprecatedService, frame));
  }

  // add a button on the eptica recipient field to change the current recipient addresse mail
  addChangeRecipientButton(responseId: string, requestId: number, parentRef: any): void {
    this.doInEpticaFrame(frame =>
      EpticaHelper.addRecipientButton(responseId, frame, requestId, this.$i18next, this.contactFacade, this.profileService, parentRef)
    );
  }

  // add a layer with multiple action buttons in the eptica's editor
  addEditorActionButtons(requestId: string, buttonList: EpticaButtonActionTypes[]): void {
    this.doInEpticaFrame(frame => EpticaHelper.addLayerButtons(this.$i18next, this.sessionDeprecatedService, frame, requestId, buttonList));
  }

  addPJEventsForAnswer(callback: any): void {
    return this.getInEpticaFrame(
      (frame: HTMLIFrameElement) =>
        EpticaHelper.addPJEvents(
          frame,
          callback,
          this.$log,
          this.toastDeprecatedService,
          this.$i18next,
          EpticaDropZoneElements.ANSWER_REQUEST
        ),
      undefined
    );
  }

  addPJEventsForDelegate(callback: any): void {
    return this.getInEpticaFrame(
      (frame: HTMLIFrameElement) =>
        EpticaHelper.addPJEvents(
          frame,
          callback,
          this.$log,
          this.toastDeprecatedService,
          this.$i18next,
          EpticaDropZoneElements.DELEGATE_REQUEST
        ),
      undefined
    );
  }

  addPJEventsForAskForHelp(callback: any): void {
    return this.getInEpticaFrame(
      (frame: HTMLIFrameElement) =>
        EpticaHelper.addPJEvents(
          frame,
          callback,
          this.$log,
          this.toastDeprecatedService,
          this.$i18next,
          EpticaDropZoneElements.CREATE_ASK_FOR_HELP_REQUEST
        ),
      undefined
    );
  }

  removePJEvents(): void {
    return this.getInEpticaFrame((frame: HTMLIFrameElement) => EpticaHelper.removePJEvents(frame, this.$log), undefined);
  }

  removePJEventsAskForHelp(): void {
    return this.getInEpticaFrame(
      (frame: HTMLIFrameElement) =>
        EpticaHelper.removePJEventsForElement(frame, this.$log, EpticaDropZoneElements.CREATE_ASK_FOR_HELP_REQUEST),
      undefined
    );
  }

  removePJEventsDelegate(): void {
    return this.getInEpticaFrame(
      (frame: HTMLIFrameElement) => EpticaHelper.removePJEventsForElement(frame, this.$log, EpticaDropZoneElements.DELEGATE_REQUEST),
      undefined
    );
  }

  // expand all the comments in the default right format according to the hasHtml attribute
  expandComments(): void {
    // expand all the comments
    this.doInEpticaFrame(frame => {
      EpticaHelper.checkDefaultHasHtmlState(frame);
    });
  }

  simulateClickOnSelection(): void {
    this.doInEpticaFrame(frame => EpticaHelper.simulateClickOnSelection(frame, 'button.fileBrowse, button.fileBrowseAnswer'));
  }

  // change the recipient on Eptica's side
  changeRequestCustomer(newIasCustomer: EpticaEnterpriseAgentExposed): void {
    this.doInEpticaFrame(frame => EpticaHelper.changeRequestCustomer(frame, newIasCustomer));
  }

  simulateClickOnUploadDelegate(): void {
    this.doInEpticaFrame(frame => EpticaHelper.simulateClickOnSelection(frame, 'button.fileBrowseDelegate'));
  }

  simulateClickOnUploadAskForHelp(): void {
    this.doInEpticaFrame(frame => EpticaHelper.simulateClickOnSelection(frame, 'button.fileBrowseAskForHelp'));
  }

  // add the txt to eptica's editor
  addTextToEditor(txt: string, $i18next: i18n): void {
    this.doInEpticaFrame(frame => EpticaHelper.addTxtToEditor(frame, txt, $i18next));
  }

  showElement(selectorName: string) {
    this.doInEpticaFrame(frame => EpticaHelper.showElement(frame, selectorName));
  }

  hideElement(selectorName: string) {
    this.doInEpticaFrame(frame => EpticaHelper.hideElement(frame, selectorName));
  }

  toggleRecipientAddress(show: boolean) {
    this.doInEpticaFrame(frame => EpticaHelper.toggleRecipientAddress(frame, show));
  }

  hideAttachmentInAnswerZone() {
    this.doInEpticaFrame(frame => EpticaHelper.hideAttachmentInAnswerZone(frame));
  }

  clearDraftAction(): void {
    this.doInEpticaFrame(frame => EpticaHelper.clearDraftAction(frame));
  }

  getAllRecipients(blackListMailAddress: string): string[] {
    const addresses = this.getAllRecipientsInternal(blackListMailAddress);
    return [...addresses.to, ...addresses.cc];
  }

  replyAllAction(blackListMailAddress: string): any {
    const addresses = this.getAllRecipientsInternal(blackListMailAddress);
    this._replyAllRecipients$.next(addresses);
  }

  private getAllRecipientsInternal(blackListMailAddress: string): { to: string[]; cc: string[] } {
    let addresses: { to: string[]; cc: string[] } = this.getInEpticaFrame(
      frame => EpticaHelper.getReplyAllAddresses(frame, blackListMailAddress),
      {}
    );
    return { to: [...new Set(addresses.to)], cc: [...new Set(addresses.cc)] };
  }

  clearDropZone(): void {
    this.doInEpticaFrame(frame => EpticaHelper.clearDropZone(frame));
  }

  removeAttachmentFileForAnswer(file: IEpticaExctractAttachment): void {
    this.doInEpticaFrame(frame => EpticaHelper.removeAttachmentFile(frame, this.$log, file, EpticaDropZoneElements.ANSWER_REQUEST));
  }

  removeAttachmentFileForAskForHelp(file: IEpticaExctractAttachment): void {
    this.doInEpticaFrame(frame =>
      EpticaHelper.removeAttachmentFile(frame, this.$log, file, EpticaDropZoneElements.CREATE_ASK_FOR_HELP_REQUEST)
    );
  }

  removeAttachmentFileForDelegate(file: IEpticaExctractAttachment): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.removeAttachmentFile(frame, this.$log, file, EpticaDropZoneElements.DELEGATE_REQUEST);
      EpticaHelper.removeAttachmentFile(frame, this.$log, file, EpticaDropZoneElements.CREATE_DELEGATE);
    });
  }

  addUploadFiles(files: File[]): void {
    this.doInEpticaFrame(frame => EpticaHelper.addUploadFiles(frame, files));
  }

  // preview the email
  preview(): void {
    this.doInEpticaFrame(frame => EpticaHelper.preview(frame));
  }

  // print the email
  print(): void {
    this.doInEpticaFrame(frame => EpticaHelper.print(frame));
  }

  setSubjectContactStyle(): void {
    this.doInEpticaFrame(frame => EpticaHelper.setContactStyleToField(frame, 'formAnswerRequest_answerMessage_subject'));
  }

  setCCContactStyle(): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.hideFieldInputContainerParent(frame, 'formAnswerRequest_answerMessage_ccRecipientsForView');
      EpticaHelper.hideFieldInputContainerParent(frame, 'answerMessage.ccRecipientsForView.link');
    });
  }

  setBCCContactStyle(): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.hideFieldInputContainerParent(frame, 'formAnswerRequest_answerMessage_bccRecipientsForView');
      EpticaHelper.hideFieldInputContainerParent(frame, 'answerMessage.bccRecipientsForView.link');
    });
  }

  setRecipientContactStyle(): void {
    this.doInEpticaFrame(frame => EpticaHelper.setContactStyleToField(frame, 'formAnswerRequest_answerMessage_toRecipientsForView'));
  }

  setChannelContactStyle(): void {
    this.doInEpticaFrame(frame => EpticaHelper.setContactStyleToField(frame, 'formAnswerRequest_answerRequest_message_outgoingChannel'));
  }

  // move the suject field from the mail to the header
  moveSubjectInHeader(): string {
    return this.getInEpticaFrame(frame => EpticaHelper.moveSubjectInHeader(frame), None).getOrElse('');
  }

  // Add two icons for print and preview
  customizeAnswerDisplay(): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.customizeAnswerDisplay(frame, this.$i18next, this.sessionDeprecatedService, ChannelDirection.INCOMING);
    });
  }

  replaceEpticaButton(buttonId: string, action: () => IPromise<EpticaData>, conditionMethod: (frame: HTMLIFrameElement) => boolean): void {
    this.doInEpticaFrame(frame => EpticaHelper.replaceEpticaButton(buttonId, frame, action, conditionMethod));
  }

  confirmCloseAssists(): IPromise<EpticaData> {
    return this.doPromiseInEpticaFrame(frame =>
      EpticaHelper.confirmCloseAssists(this.$q, this.$i18next, this.$log, this.toastDeprecatedService, this.sessionDeprecatedService, frame)
    );
  }

  isOnPage(pagename: string): boolean {
    return this.getInEpticaFrame(frame => EpticaHelper.getEpticaData(frame), None)
      .map(epticaData => epticaData.pageName === pagename)
      .getOrElse(false);
  }

  sendAskForHelpAnswer(epticaRequestId: number): IPromise<IEpticaJsonResponse> {
    return this.doPromiseInEpticaFrame(frame =>
      EpticaHelperJson.askForHelpAnswerClick(this.sw, frame).then(f => {
        this.epticaDeprecatedService.askForHelpAnswer(epticaRequestId);
        return f;
      })
    );
  }

  fillTransferToEmail(email: string, comment: string): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.transfertSetSpecialist(frame, email);
      EpticaHelper.transfertSetComment(frame, comment);
      EpticaHelper.transfertSetKindOfForward(frame, 'external');
    });
  }

  fillTransfertToAgent(iasId: string, comment: string): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.transfertSetKindOfForward(frame, 'agent');
      EpticaHelper.transfertSetAgent(frame, iasId);
      EpticaHelper.transfertSetComment(frame, comment);
      EpticaHelper.transfertSetAccountType(frame, 1);
    });
  }

  fillAssistanceToEmail(email: string, comment: string): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.assistanceSetSpecialist(frame, email);
      EpticaHelper.assistanceSetComment(frame, comment);
      EpticaHelper.assistanceSetKindOfForward(frame, 'external');
    });
  }

  fillAssistanceToAgent(iasId: string, comment: string): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.assistanceSetKindOfForward(frame, 'agent');
      EpticaHelper.assistanceSetAgent(frame, iasId);
      EpticaHelper.assistanceSetComment(frame, comment);
      EpticaHelper.assistanceSetAccountType(frame, 1);
    });
  }

  selectAllMessageRequestHistory(): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.selectAllMessageRequestHistory(frame);
    });
  }

  getIncludedMessages(): string[] {
    return this.getInEpticaFrame(frame => EpticaHelper.getIncludedMessages(frame), []);
  }

  doPromiseInEpticaFrame<T>(fn: (frame: HTMLIFrameElement) => IPromise<T>): IPromise<T> {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => fn(frame))
      .getOrElseFunc(() => this.$q.reject());
  }

  override doInEpticaFrame(fn: (frame: HTMLIFrameElement) => void): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => fn(frame));
  }

  override getInEpticaFrame<T>(fn: (frame: HTMLIFrameElement) => T, defaultValue: T): T {
    return this.extApp
      .getApplicationContainer(this.frameId)
      .map(frame => fn(frame))
      .getOrElse(defaultValue);
  }

  printMessageClientWithHtmlFormat(): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => EpticaHelper.printMessageClientWithHtmlFormat(frame));
  }

  toggleConversationalMode(): void {
    this.extApp.getApplicationContainer(this.frameId).sideEffect(frame => EpticaHelper.toggleConversationalMode(frame));
  }

  protected internalFinish(): void {
    this.epticaDeprecatedService.removeIncomingRequest(this.getId());
  }

  saveDraft(): IPromise<EpticaData> {
    return this.doPromiseInEpticaFrame(frame => {
      if (EpticaHelper.validateOutgoingSend(frame, this.$log)) {
        return EpticaHelper.simulateClickAndAttachmentsAndLoad(
          this.$q,
          this.$i18next,
          this.$log,
          this.toastDeprecatedService,
          this.sessionDeprecatedService,
          frame,
          'formAnswerRequest_commonRequest_saveDraft',
          true
        );
      }
      return this.$q.reject();
    });
  }

  loadDraftAttachments(callback: any): void {
    this.extApp.getApplicationContainer(this.frameId).map(frame => {
      let attachments: IEpticaExctractAttachment[] = EpticaHelper.getAttachments(frame);
      attachments.forEach((file: IEpticaExctractAttachment) => callback(file));
    });
  }

  switchCommentFormat(elemId: string): void {
    this.doInEpticaFrame(frame => {
      EpticaHelper.epticaJQuery(frame)(`[name=messageSelectionRequestHistory][value=${elemId}]`)
        .parents('.request_event_type_client_message')
        .find('.switchable_content_switch_icon')
        .click();
    });
  }

  preAddRequest(): void {
    this.epticaDeprecatedService.addPJEventsForLoader();
  }

  checkFirstExistingSelector(...selector): string {
    return this.getInEpticaFrame(frame => {
      for (let s = 0; s < selector.length; s++) {
        const elem = EpticaHelper.getEpticaElementBySelector(frame, selector[s]);
        if (!!elem) {
          return selector[s];
        }
      }
      return '';
    }, '');
  }

  isValidForm(): boolean {
    let isValidForm = false;
    this.doInEpticaFrame(frame => {
      isValidForm = EpticaHelper.callEpticaFn<boolean>(frame, this.$log, 'validateForm').getOrElse(false);
      if (!isValidForm) {
        EpticaHelper.displayValidationMessages(this.toastDeprecatedService, frame, 'request-answer');
      }
    });
    return isValidForm;
  }

  toggleMailHistoryCheckBox(elemId: string, checked: boolean): void {
    this.doInEpticaFrame(frame => {
      const targetCheckBox = EpticaHelper.epticaJQuery(frame)(`[name=messageSelectionRequestHistory][value=${elemId}]`);
      if (checked === undefined) {
        targetCheckBox.click();
      } else if (checked) {
        targetCheckBox.prop('checked', true);
      } else {
        targetCheckBox.prop('checked', false);
      }
    });
  }
}
